import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { HttpResponse } from '@angular/common/http';
import { TimeoutError, timeout } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { ApiRequestService } from '@app/_services/api-Services/api-request.service';
import { APP_LOGIN_URL, APP_NAME, APP_WEBSITE_URL } from '@app/shared/utilities/app-info';
import { environment } from 'src/environments/environment';
import { AuthLayoutComponent } from '../shared/auth-layout.component';
import {
  MAX_VERIFY_ATTEMPTS,
  OTP_VALIDITY_MINUTES,
  RESEND_COOLDOWN_SECONDS,
  TRIAL_ENDPOINTS,
  VERIFY_TIMEOUT_MS,
  VerifyOutcome,
  classifyVerifyFailure,
  clearTrialSignup,
  extractApiMessage,
  loadTrialSignup,
  toTrialRequest
} from '../shared/trial-signup';

interface VerifyFailure {
  message: string;
  outcome: VerifyOutcome;
}

/**
 * Step 2 of the trial registration flow. Verifying the code provisions the
 * company synchronously on the server (database, schema, admin user, welcome
 * email), so this screen shows a long-running progress state and maps every
 * documented failure to a next step.
 */
@Component({
  selector: 'app-verify-otp',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, AuthLayoutComponent],
  templateUrl: './verify-otp.component.html',
  styleUrls: ['./verify-otp.component.scss']
})
export class VerifyOtpComponent implements OnInit, OnDestroy {
  readonly appName = APP_NAME;
  readonly websiteUrl = APP_WEBSITE_URL;
  readonly loginUrl = APP_LOGIN_URL;
  readonly otpValidityMinutes = OTP_VALIDITY_MINUTES;
  readonly sideHeading = 'One last step to activate your account';
  readonly sideBullets = [
    `We emailed you a 6-digit code, valid for ${OTP_VALIDITY_MINUTES} minutes`,
    'Verifying it creates your company environment',
    'Your welcome email will contain your company code'
  ];

  otpForm!: FormGroup;
  email = '';
  isLoading = false;
  isResending = false;
  isSuccessState = false;
  successMessage = '';
  infoMessage = '';
  failure: VerifyFailure | null = null;
  attemptsLeft = MAX_VERIFY_ATTEMPTS;
  resendCooldown = 0;

  private cooldownTimer: ReturnType<typeof setInterval> | null = null;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private apiRequestService: ApiRequestService,
    private toastrService: ToastrService
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      this.email = params['email'] || loadTrialSignup()?.email || '';
      if (!this.email) {
        this.failure = {
          message: 'We could not find your registration details. Please start again.',
          outcome: 'resend'
        };
      }
    });

    this.otpForm = this.fb.group({
      otp: ['', [Validators.required, Validators.pattern('^[0-9]{6}$')]]
    });
  }

  ngOnDestroy(): void {
    this.clearCooldown();
  }

  get f() {
    return this.otpForm.controls;
  }

  /** Resend needs the saved form: a resend is a re-POST of the whole registration. */
  get canResend(): boolean {
    return !!loadTrialSignup() && this.resendCooldown === 0 && !this.isResending && !this.isLoading && !this.isTerminal;
  }

  /** After an infrastructure failure the user must not loop; support repairs it. */
  get isTerminal(): boolean {
    return this.failure?.outcome === 'support' || this.failure?.outcome === 'login';
  }

  get verifyDisabled(): boolean {
    return this.isLoading || this.attemptsLeft <= 0 || this.isTerminal || !this.email;
  }

  onSubmit(): void {
    this.failure = null;
    this.infoMessage = '';
    this.otpForm.markAllAsTouched();

    const otp: string = (this.otpForm.get('otp')?.value ?? '').trim();

    // Development-only shortcut so the done screen can be reviewed without a
    // live mail server. Never active in production builds.
    if (!environment.production && otp === '000011') {
      this.enterSuccessState('Your trial environment request has been successfully created.');
      return;
    }

    if (this.otpForm.invalid) {
      this.failure = { message: 'Please enter the 6-digit code from your email.', outcome: 'retry' };
      return;
    }
    if (this.verifyDisabled) {
      return;
    }

    this.isLoading = true;
    const endpoint = `${TRIAL_ENDPOINTS.verifyOtp}?CompanyPersonEmail=${encodeURIComponent(this.email)}&OTP=${encodeURIComponent(otp)}`;

    this.apiRequestService
      .postRequest<any>(endpoint, {})
      .pipe(timeout(VERIFY_TIMEOUT_MS))
      .subscribe({
        next: (response: HttpResponse<any>) => {
          this.isLoading = false;
          const body = response.body;
          const message: string =
            (body && typeof body === 'object' ? body.message : body) || 'Your trial environment has been created.';

          // A provisioning failure still comes back as HTTP 200 with success:false.
          if (body && typeof body === 'object' && body.success === false) {
            this.handleFailure(message);
            return;
          }
          this.enterSuccessState(message);
        },
        error: (error) => {
          this.isLoading = false;
          if (error instanceof TimeoutError) {
            this.failure = {
              message:
                'This is taking longer than expected. Your environment may still be finishing: check your inbox for a welcome email over the next few minutes before trying again.',
              outcome: 'timeout'
            };
            return;
          }
          this.handleFailure(extractApiMessage(error, 'Verification failed. Please try again.'));
        }
      });
  }

  resendOTP(): void {
    if (!this.canResend) {
      return;
    }
    const saved = loadTrialSignup();
    if (!saved) {
      return;
    }

    this.failure = null;
    this.infoMessage = '';
    this.isResending = true;

    this.apiRequestService.postRequest<any>(TRIAL_ENDPOINTS.sendOtp, toTrialRequest(saved)).subscribe({
      next: (response: HttpResponse<any>) => {
        this.isResending = false;
        this.attemptsLeft = MAX_VERIFY_ATTEMPTS;
        this.otpForm.reset();
        this.infoMessage = response.body?.message || `A new code has been sent to ${this.email}. Any previous code no longer works.`;
        this.startResendCooldown();
      },
      error: (error) => {
        this.isResending = false;
        this.failure = {
          message: extractApiMessage(error, 'We could not resend the code. Please try again in a moment.'),
          outcome: 'resend'
        };
      }
    });
  }

  /** Back to the form with everything pre-filled; `company` jumps to the company step. */
  editRegistration(section: 'details' | 'company'): void {
    this.router.navigate(['/auth/sign-up'], { queryParams: { resume: section } });
  }

  startOver(): void {
    clearTrialSignup();
    this.router.navigate(['/auth/sign-up']);
  }

  private handleFailure(message: string): void {
    const outcome = classifyVerifyFailure(message);
    if (outcome === 'retry') {
      this.attemptsLeft = Math.max(0, this.attemptsLeft - 1);
    }
    if (outcome === 'login' || outcome === 'support') {
      // Terminal for this registration: the company row may already exist.
      clearTrialSignup();
    }
    this.failure = { message, outcome };
    this.toastrService.error(message, 'Verification failed');
  }

  private enterSuccessState(message: string): void {
    this.isSuccessState = true;
    this.successMessage = message;
    this.toastrService.success('Your trial environment has been created.', 'Success');
    this.otpForm.reset();
    clearTrialSignup();
    this.clearCooldown();
  }

  private startResendCooldown(): void {
    this.resendCooldown = RESEND_COOLDOWN_SECONDS;
    this.cooldownTimer = setInterval(() => {
      this.resendCooldown--;
      if (this.resendCooldown <= 0) {
        this.clearCooldown();
      }
    }, 1000);
  }

  private clearCooldown(): void {
    if (this.cooldownTimer) {
      clearInterval(this.cooldownTimer);
      this.cooldownTimer = null;
    }
    this.resendCooldown = 0;
  }
}
